import React from 'react';
import styled from 'styled-components';

import { ClipLoader } from 'react-spinners';

const HeroContainer = styled.div`
    background-image: url(https://i.pinimg.com/originals/f8/dd/eb/f8ddeb5320e3bd3d3bbbfd1ce99136b8.jpg);
    background-size: cover;
    background-position: center center;
    position: relative;
    height: 80vh;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #f8f8f8;
`;

const Overlay = styled.div`
    position: absolute;
    height: 100%;
    width: 100%;
    background-color: rgba(0,0,0,0.5);
`;

const HeroContent = styled.div`
    z-index: 1;
    width: 100%;
    padding: 0 1rem;
`;

const HeroTitle = styled.h1`
    font-size: 3rem;
    font-weight: bold;
    font-family: Merriweather, serif;
    text-align: center;
    text-shadow: 0px 2px 5px rgba(0,0,0,1);
    margin-bottom: 0.5rem;

    @media (max-width: 480px) {
        font-size: 2rem;
    }
`;

const HeroSubtitle = styled.h2`
    font-size: 1.25rem;
    font-weight: 500;
    font-family: Lexend, serif;
    text-align: center;
    text-shadow: 0px 2px 5px rgba(0,0,0,1);
    margin-top: 1rem;

    @media (max-width: 480px) {
        font-size: 1rem;
        margin-top: 0.5rem;
    }
`;

const HeroForm = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 1rem;
`;

const HeroFormInputContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: fit-content;

    @media (max-width: 480px) {
        flex-direction: column;
    }
`;

const HeroFormButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    margin-top: 1rem;
`;

const HeroFormInput = styled.input`
    font-size: 1.25rem;
    font-weight: bold;
    font-family: Lexend, serif;
    text-align: center;
    border: none;
    border-radius: 0.25rem;
    background-color: #f8f8f8;
    outline: 5px solid #56050D;
    color: #0a0a0a;
    padding: 0.5rem 1rem;

    &::placeholder {
        color: #2a2a2a;
    }

    @media (max-width: 480px) {
        margin: 0.5rem 0;
        padding: 0.35rem 0rem;
    }
`;

const HeroFormButton = styled.button`
    font-size: 1.25rem;
    font-weight: 400;
    font-family: Lexend, serif;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    letter-spacing: 0.5rem;
    background-color: #77363d;
    color: #dadada;
    padding: 0.5rem 2rem;
    border-radius: 0.5rem;
    cursor: pointer;
    margin-top: 1rem;
    box-shadow: 0px 4px 5px 2px rgba(20, 20, 20, 0.5);
    text-transform: uppercase;
    transition: all 0.25s ease-in-out;

    &:hover {
        background-color: #5a2328;
        color: #f8f8f8;
    }

    &:disabled {
        box-shadow: none;
        background-color: #dadada;
        color: #2a2a2a;
        cursor: not-allowed;
    }
`;

const HeroFormSecondaryButton = styled(HeroFormButton)`
    padding: 0.35rem 1rem;
    font-size: 1.15rem;
    font-weight: 400;
    letter-spacing: 0.25rem;
    background-color: #77363d;

    &:hover {
        background-color: #5a2328;
    }
`;

const HeroFormInputDividerText = styled.span`
    font-size: 1.25rem;
    font-weight: bold;
    font-family: Lexend, serif;
    text-align: center;
    text-shadow: 0px 2px 5px rgba(0,0,0,1);
    margin: 0 1rem;

    @media (max-width: 480px) {
        margin: 0.25rem 0;
    }
`;

const Hero = ({ isLoading, name, setName, email, setEmail, handleSubmit }) => {
    const isEmptyInput = !email && !name;
    const isNameInvalid = () => {
        if (!name) return false;
        const parts = name.trim().replace(/\s+/g, ' ').split(' ');
        // Ensure there are exactly 2 parts (first name and last name)
        if (parts.length !== 2) return true;
        // Ensure each part is at least 2 characters long
        if (parts[0].length < 2 || parts[1].length < 2) return true;
        return false;
    };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailInvalid = email && !emailRegex.test(email);

    const handleOpenRegistry = () => {
        // Open the registry in a new tab
        const url = 'https://www.zola.com/registry/simronandsunny';
        window.open(url, '_blank');
    }

    return (
        <HeroContainer>
            <Overlay />
            <HeroContent>
                <HeroTitle>Our Wedding Journey Includes You</HeroTitle>
                <HeroSubtitle>Please enter your full name or email to view your invitations and RSVP to the celebrations</HeroSubtitle>
                <HeroForm>
                    <HeroFormInputContainer>
                        <HeroFormInput type="email" disabled={isLoading} placeholder="Email" value={email} onChange={(event) => setEmail(event.target.value)} />
                        <HeroFormInputDividerText>or</HeroFormInputDividerText>
                        <HeroFormInput type="text" disabled={isLoading} placeholder="Full Name" value={name} onChange={(event) => setName(event.target.value)} />
                    </HeroFormInputContainer>
                    <HeroFormButtonContainer>
                        <HeroFormButton onClick={handleSubmit} disabled={isEmptyInput || isNameInvalid() || isEmailInvalid || isLoading}>
                            <ClipLoader color="#2a2a2a" size={16} loading={isLoading} cssOverride={{ marginRight: '0.75rem' }} />
                            Find RSVP
                        </HeroFormButton>

                        <HeroFormSecondaryButton onClick={handleOpenRegistry}>
                            Give a Gift
                        </HeroFormSecondaryButton>
                    </HeroFormButtonContainer>
                </HeroForm>
            </HeroContent>
        </HeroContainer>
    );
};

export default Hero;